import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Company identity block — legal details, intentionally not translated.
const CompanyBlock = () => (
  <div className="mt-4 space-y-1 text-neutral-700">
    <div className="font-semibold">Footpoolevents.ch</div>
    <div>Petar Strajnic</div>
    <div>Winggel 13</div>
    <div>9468 Sax</div>
    <div>Schweiz / Suisse / Svizzera</div>
  </div>
);

const EmailLine = () => (
  <div className="mt-6 text-sm text-neutral-600">
    E-Mail:{" "}
    <a className="text-[#0055FF]" href="mailto:info@footpoolevents.ch">
      info@footpoolevents.ch
    </a>
  </div>
);

const LegalModal = ({ open, onClose, kind }) => {
  const { t, lang } = useLanguage();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const legal = t.legal;
  const privacy = legal.privacy;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={`legal-modal-${kind}`}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <button
        aria-label={legal.closeAria}
        onClick={onClose}
        className="absolute inset-0 bg-neutral-900/70 backdrop-blur-sm"
      />
      <div className="relative max-w-xl w-full max-h-[85vh] overflow-auto rounded-3xl bg-white p-7 md:p-9 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          data-testid="legal-close"
          aria-label={legal.closeAria}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-neutral-600 hover:bg-neutral-100"
        >
          <X className="w-5 h-5" />
        </button>

        {kind === "impressum" ? (
          <>
            <h3 className="font-heading text-xl font-bold text-neutral-900">
              {legal.impressum.title}
            </h3>
            <CompanyBlock />
            <EmailLine />
            <p className="mt-6 text-xs text-neutral-500 leading-relaxed">
              {legal.impressum.note}
            </p>
          </>
        ) : (
          <>
            <h3 className="font-heading text-xl font-bold text-neutral-900">
              {privacy.title}
            </h3>
            <p className="mt-2 text-xs text-neutral-500">{privacy.stand}</p>
            <div className="mt-5 space-y-5 text-sm text-neutral-700 leading-relaxed">
              {privacy.sections.map((s) => (
                <div key={s.h}>
                  <div className="font-semibold text-neutral-900">{s.h}</div>
                  {s.paras.map((p, i) => (
                    <p key={i} className="mt-1.5 whitespace-pre-line">{p}</p>
                  ))}
                  {s.links && s.links.length > 0 && (
                    <ul className="mt-1.5 space-y-1">
                      {s.links.map((l) => (
                        <li key={l.url}>
                          {l.label}:{" "}
                          <a
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0055FF] underline underline-offset-2 break-all hover:text-[#0044dd]"
                          >
                            {l.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const useLegalModal = () => {
  const [state, setState] = useState({ open: false, kind: null });
  return {
    openImpressum: () => setState({ open: true, kind: "impressum" }),
    openPrivacy: () => setState({ open: true, kind: "privacy" }),
    close: () => setState({ open: false, kind: null }),
    Modal: () => (
      <LegalModal
        open={state.open}
        onClose={() => setState({ open: false, kind: null })}
        kind={state.kind}
      />
    ),
  };
};

export default LegalModal;
